import { useMemo, useState } from 'react';
import {
  MaterialReactTable,
  MRT_SelectCheckbox,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from 'material-react-table';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Grid,
  IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { emptyCellObject, EstadoGestionInventarioENUM, FILTRO_ESTADO_GESTION_INVENTARIO, formatDateWithTime, gridSizeMdLg1, gridSizeMdLg2, gridSizeMdLg3, OPCIONES_ESTADO_GESTION_INVENTARIO_EDITABLE, sanitizeTodos, useLoaders, useTableFilter, useTableServerSideFiltering } from '@/shared';
import { GestionInventarioCuadre, PayloadEgresoGestionInventarioCuadre } from '../../types';
import { CustomAutocompleteArrStringNoForm } from '@/shared/components/ui';
import { useFetchGetGestionInventarioCuadre, useFetchGetGestionInventarioCuadreEstadistica, useFetchPostEgresoGestionInventarioCuadre, useFetchPutGestionInventarioCuadre } from '@/shared/actions';
import { useUiStore } from '@/store/ui';
import { Refresh } from "@mui/icons-material";
import { TotalItemsCard } from '../total-item-card/TotalItemsCard';
import ChecklistIcon from '@mui/icons-material/Checklist';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ErrorIcon from '@mui/icons-material/Error';
import { openGenericObjectModalAsync } from '@/shared/components/ui/ModalGenericObject/useGenericObjectModalStore';
import { getCuadreInventario } from '@/shared/actions/erp/inventario/cuadre.actions';
import { fieldsCuadre, fieldsEgreso } from '@/shared/utils/fields-modal-generic-object';
import {getEgresos } from '@/shared/actions/erp';

// Colores de fondo según el estado
const BG_COLORS: Record<string, string> = {
  FIBRAPOINT: '#C4E1F6',
  FALTANTE: '#FF5656',
  FACTURADO: '#FF9D3D',
};

// Mapeo de estilos basados en si es "NO REQUIERE" o no
const getEstilosPorEstado = (esNoRequiere: boolean) => ({
  color: '#000000',
  outline: esNoRequiere ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
  outlineHover: esNoRequiere ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
  icon: esNoRequiere ? 'rgba(0, 0, 0, 0.54)' : '#fff',
});

const ESTADOS_OPCIONES = ["NO REQUIERE", "FIBRAPOINT", "FALTANTE", "FACTURADO"];

type TablaGestionInventarioProps = {
  estado: string
}

export const TablaGestionInventario = ({ estado }: TablaGestionInventarioProps) => {

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectedEstado, setSelectedEstado] = useState<string>(EstadoGestionInventarioENUM.TODOS);

  // hooks

  const setConfirmDialog = useUiStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiStore((s) => s.setConfirmDialogIsOpen);

  const {
    pagination,
    setPagination,
  } = useTableFilter();

  const {
    filterObject,
    columnFilters,
    setColumnFilters,
  } = useTableServerSideFiltering();

  // fetch

  const {
    data,
    isLoading,
    isRefetching,
    refetch,
  } = useFetchGetGestionInventarioCuadre({
    params: {
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      estado,
      tipo_gestion: sanitizeTodos(selectedEstado),
      ...filterObject,
    }
  });

  const {
    data: estadistica,
    isLoading: isLoadingEstadistica,
    refetch: refetchEstadistica
  } = useFetchGetGestionInventarioCuadreEstadistica({
    params: {
      estado: estado,
      tipoGestion: sanitizeTodos(selectedEstado) as any,
    },
  });

  const {
    mutateAsync: postEgresoGestion,
    isPending: isPostingEgresoGestion,
  } = useFetchPostEgresoGestionInventarioCuadre({})


  // update
  const { mutate: updateGestion } = useFetchPutGestionInventarioCuadre({});

  // Manejador para el cambio de estado en la fila
  const handleEstadoChange = (rowOriginal: GestionInventarioCuadre, nuevoEstado: string) => {
    updateGestion({
      id: rowOriginal.id_gestion_inventario_cuadre,
      data: {
        tipo_gestion: nuevoEstado,
      } as any,
    });
  };


  const columns = useMemo<MRT_ColumnDef<GestionInventarioCuadre>[]>(
    () => [
      {
        id: "referencia_cuadre",
        accessorKey: 'referencia_cuadre',
        header: 'Cuadre',
        Cell: ({ row }) => {

          const codigo = row.original.referencia_cuadre;

          return <Button
            onClick={() => {

              openGenericObjectModalAsync({
                fetcher: async () => {
                  const resp = await getCuadreInventario({
                    codigo: codigo
                  })
                  return resp.data.data[0]
                },
                fields: fieldsCuadre,
                title: "Información",
                emptyFallback: "—",
              });

            }}
          >
            {codigo}
          </Button>
        }
      },
      {
        accessorKey: 'cantidad',
        header: 'Cant.',
        size: 80,
      },
      {
        id: 'producto__unidad_medida',
        accessorKey: 'producto.unidad_medida',
        header: 'Unidad',
        filterVariant: 'select',
        filterSelectOptions: ['METROS', 'UNIDADES'],
      },
      {
        id: 'serie__serie',
        accessorKey: 'serie.serie',
        header: 'Serie',
        Cell: ({ row }) => emptyCellObject(row.original.serie, 'serie')
      },
      {
        id: 'producto__referencia_producto',
        accessorKey: 'producto.referencia_producto',
        header: 'Cod. Producto'
      },
      {
        id: 'producto__descripcion',
        accessorKey: 'producto.descripcion',
        header: 'Descripción'
      },
      {
        id: "tipo_gestion",
        accessorKey: 'tipo_gestion',
        header: 'Tipo Gestión',
        filterVariant: 'select',
        filterSelectOptions: OPCIONES_ESTADO_GESTION_INVENTARIO_EDITABLE,
        Cell: ({ cell, row }) => {

          // 1. Obtenemos el valor una sola vez
          const valor = cell.getValue<string>();

          // 2. Calculamos las variables que usará el sx
          const backgroundColor = BG_COLORS[valor] || '#fff';
          const config = getEstilosPorEstado(valor === 'NO REQUIERE');

          return <Select
            disabled={estado === 'EN GESTION'}
            value={cell.getValue<string>()}
            size="small"
            fullWidth
            sx={{
              bgcolor: backgroundColor,
              color: config.color,

              '.MuiOutlinedInput-notchedOutline': {
                borderColor: config.outline,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: config.outlineHover,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: config.outlineHover,
              },
              '.MuiSelect-icon': {
                color: config.icon,
              }
            }}
            onClick={(e) => e.stopPropagation()} // Evita seleccionar la fila al hacer clic en el select
            onChange={(e) =>
              handleEstadoChange(row.original, e.target.value)
            }
          >
            {ESTADOS_OPCIONES.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        }
      },

      {
        id: 'observacion',
        accessorKey: 'observacion',
        header: 'Observaciones',
        Cell: ({ row }) => emptyCellObject(row.original, 'observacion')
      },
      {
        id: 'estado',
        accessorKey: 'estado',
        header: 'Estado',
      },

      {
        id: "referencia_egreso",
        accessorKey: 'referencia_egreso',
        header: 'Referencia Egreso',
        //Cell: ({ row }) => emptyCellObject(row.original, 'referencia_egreso')
        Cell: ({ row }) => {

          const codigo = row.original.referencia_egreso;

          if(!codigo){
            return "—";
          }

          return <Button
            onClick={() => {

              openGenericObjectModalAsync({
                fetcher: async () => {
                  const resp = await getEgresos({
                    codigo: codigo
                  })
                  return resp.data.data[0]
                },
                fields: fieldsEgreso,
                title: "Información",
                emptyFallback: "—",
              });

            }}
          >
            {codigo}
          </Button>
        }
      },
      {
        id: "fecha_creacion",
        accessorKey: 'fecha_creacion',
        header: 'Fecha Creación',
        Cell: ({ row }) => formatDateWithTime(row.original.fecha_creacion)
      },
    ],
    [],
  );

  const handleEnviarGestion = async () => {

    setConfirmDialogIsOpen(true);
    const filasSeleccionadas = table.getSelectedRowModel().rows.map(row => row.original);
    setConfirmDialog({
      isOpen: true,
      title: 'Confirmar',
      subtitle:
        `¿Deseas completar este envío? Esto registrará un egreso en el inventario actual. Se han seleccionado ${filasSeleccionadas.length} elementos para realizar el movimiento a GESTION.`,
      async onConfirm() {
        setConfirmDialogIsOpen(false);

        // Obtenemos los objetos completos de las filas seleccionadas

        const payload: PayloadEgresoGestionInventarioCuadre[] = filasSeleccionadas.map((item) => {
          const tieneSerie = item.serie !== null;
          const usaMetros = item.producto.unidad_medida.toUpperCase() === 'METROS';
          const textoSerie = item.serie?.serie || '';

          const objetoTransformado = {
            codigo: item.producto.referencia_producto,
            // cuando es metro por defecto siempre se envia una serie
            cantidad: usaMetros ? "1" : String(item.cantidad),
            contiene_serie: tieneSerie,
            usa_metros: usaMetros,
            series: tieneSerie ? [textoSerie] : [],
            cantidad_metros: usaMetros ? String(item.cantidad) : '0',
            series_metros: usaMetros && tieneSerie ? { [textoSerie]: String(item.cantidad) } : {},
            serie_generado_por: 'SELECCION_MANUAL',
            referencia_cuadre: item.referencia_cuadre,
            id_gestion_inventario_cuadre: item.id_gestion_inventario_cuadre,
            fecha_actual_fronted: new Date().toISOString(),
            tipo_gestion: item.tipo_gestion
          };

          return objetoTransformado;
        })

        await postEgresoGestion(payload)

      },
      onClose() {
        setConfirmDialogIsOpen(false);
      },
      cancelTextBtn: 'Cancelar',
    });



  };

  const table = useMaterialReactTable({
    columns,
    data: data?.data.data || [],
    enableGlobalFilter: false,
    enableRowSelection: estado === 'EN ESPERA',
    getRowId: (row: any) => String(row.id_gestion_inventario_cuadre),
    onRowSelectionChange: setRowSelection,

    // server-side pagination
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: data?.data?.count ?? 0,

    // server-side filtering
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters as any,
    state: {
      rowSelection,
      isLoading,
      showProgressBars: isRefetching,
      pagination,
      columnFilters: columnFilters as any,
    },
    positionToolbarAlertBanner: "head-overlay",

    initialState: { density: 'comfortable' },


    // Personalización de la barra superior para el botón
    renderToolbarAlertBannerContent: ({ table, selectedAlert }) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between', // Separa el grupo de la izquierda del botón de la derecha
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Sub-contenedor para agrupar los elementos de la izquierda */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem' // Controla la separación entre el checkbox y la alerta
          }}
        >
          <MRT_SelectCheckbox table={table} />
          {selectedAlert}
        </Box>

        {/* Este botón se irá automáticamente al extremo derecho */}
        <Button
          color="primary"
          onClick={handleEnviarGestion}
          disabled={table.getSelectedRowModel().rows.length === 0}
          variant="contained"
          startIcon={<SendIcon />}
        >
          Enviar Gestión
        </Button>
      </Box>
    ),
  });

  useLoaders(
    isPostingEgresoGestion ||
    isLoadingEstadistica
  )

  return <Grid container spacing={3}
    justifyContent={'flex-end'}
    alignItems={'center'}
  >

    <Grid item {...gridSizeMdLg3}>
      <TotalItemsCard
        total={estadistica?.data?.total_facturado ?? 0}
        label="FACTURADO"
        icon={<ChecklistIcon fontSize="large" />}
      />
    </Grid>
    <Grid item {...gridSizeMdLg3}>
      <TotalItemsCard
        total={estadistica?.data?.total_fibrapoint ?? 0}
        label="FIBRAPOINT"
        icon={<RadioButtonCheckedIcon fontSize="large" />}
      />
    </Grid>
    <Grid item {...gridSizeMdLg3}>
      <TotalItemsCard
        total={estadistica?.data?.total_faltante ?? 0}
        label="FALTANTE"
        icon={<ErrorIcon fontSize="large" />}
      />
    </Grid>
    <CustomAutocompleteArrStringNoForm
      label="Seleccionar"
      required={false}
      options={FILTRO_ESTADO_GESTION_INVENTARIO}
      defaultValue={EstadoGestionInventarioENUM.TODOS}
      size={gridSizeMdLg2}
      onChangeValue={(value) => setSelectedEstado(value as string)}
    />
    <Grid item {...gridSizeMdLg1}>

      <IconButton
        color="primary"
        onClick={() => { refetch(); refetchEstadistica(); }}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: '50%',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
        }}
      >
        <Refresh />
      </IconButton>
    </Grid>

    <Grid item xs={12}>
      <MaterialReactTable table={table} />
    </Grid>
  </Grid>;
};

export default TablaGestionInventario;